/**
 * @file RequireCompanyFeature.tsx
 * @brief Route guard — blocks a company-admin route when the company has the
 *        required product turned off (e.g. interpretation scheduling).
 *
 * Pairs with the nav-hiding in companyNavConfig so a direct-URL visit to a
 * hidden section can't slip through. Purely a gate: re-enabling the product
 * restores access immediately (nothing is deleted or changed). While the
 * toggles load we show a loader rather than flashing the page. (15-07-2026)
 */

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { FullScreenLoader } from "@/components/feedback/FullScreenLoader";
import { useCompanyFeatures, type FeatureAccess } from "./api";

export function RequireCompanyFeature({
  feature,
  children,
  redirectTo = "/company",
}: {
  feature: keyof FeatureAccess;
  children: ReactNode;
  redirectTo?: string;
}) {
  const { data, isLoading } = useCompanyFeatures();

  if (isLoading) return <FullScreenLoader label="Getting things ready…" />;
  // Only an explicit `false` blocks — unknown/loading keeps access (fail-open).
  if (data && data.featureAccess[feature] === false) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}
